import React, { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { type FieldListObject } from '@fabric-ui/components';
import { emptyArray, isDeepEqual } from '@fabric-ui/utils';
import { getLocationState, useNavigate } from '@utils/AvRouter';
import { expressionToFilter, filterToExpression } from '@utils/filterUtils';
import { parseDimsWithExpression, setNestedValue } from '@utils/utils';
import { useSnackbar } from 'notistack';
import { useAvContext } from '../../context/AvContextProvider';
import { useCustomSearchParams } from '../../hooks/UseCustomSearchParams2';
import useGlobalInteractiveFilters from '../../hooks/useGlobalFilters';
import { PAGE_PATHS } from '../../types';
import { useBuildQueryParams } from '../Reports/hooks';
import { defaultTable, defaultWidget, DimensionTypes } from './constants';
import { defaultDashboard, getGlobalFiltersFromDashboardFilters, getInitialWidgetData } from './CustomDashboardsUtils';
import { getCleanRequestsFromTileWidget } from './CustomDashboardsUtils/utils';
import { useDashboard } from './hooks/useDashboard';
import { useGetDisplayName } from './hooks/useGetDisplayName';
import { MetricClickThroughSupport, useGetMetricsClickThroughSupport } from './hooks/useGetMetricsClickThroughSupport';
import useInitWidget from './hooks/useInitWidget';
import {
  AllTypeWidgets,
  BarSubType,
  CustomDashboardDto,
  LineSubType,
  PieSubType,
  PivotTableTypeWidget,
  TableSubType,
  TileSubType,
  TileWidgetVisualization,
  WidgetCategory,
} from './types';
import { FieldType, TabsType } from './types/types';

export type SetNewWidgetOptions = { resetSize?: boolean };

export type CustomDashboardContextState = {
  newWidget: AllTypeWidgets;
  newDashboard: CustomDashboardDto;
  isLoadingCustomDashboard: boolean;
  filters: any;
  expandedWidgetId: string | undefined;
  metricsWithAliases: { systemName: string; alias?: string }[];
  isDashboardChanged: boolean;
  initDashboard: CustomDashboardDto;
  globalFilters: Record<string, any>;
  isAllowedEdit: boolean;
  metricsClickThroughSupport: Record<string, MetricClickThroughSupport>;
  isLoadingMetricsClickThroughSupport: boolean;
  dashboardId?: string;
  globalInteractiveFilters: FieldListObject[];
  scrollContainerRef: RefObject<HTMLElement | null>;
};
export type CustomDashboardContextType = CustomDashboardContextState & {
  setFilters: any;
  setNewDashboard: React.Dispatch<React.SetStateAction<CustomDashboardDto>>;
  setExpandedWidgetId: React.Dispatch<React.SetStateAction<string | undefined>>;
  getDisplayName: ReturnType<typeof useGetDisplayName>;
  setInitDashboard: (dashboard: CustomDashboardDto) => void;
  setNewWidget: (v: AllTypeWidgets, options?: SetNewWidgetOptions) => void;
  initWidget: (params: {
    selectedWidgetType: PieSubType | TableSubType | BarSubType | LineSubType | TileSubType;
    selectedWidgetCategory: WidgetCategory;
    currentWidget: AllTypeWidgets;
  }) => AllTypeWidgets | {};
};

export const defaultFilters: Record<string, any> = {
  date: [],
  tab: TabsType.Data,
  dataSelectTypes: FieldType.All,
  isEditWidgetOpen: false,
  isEditMode: false,
};

const getDefaultWidget = () => defaultWidget;

export const defaultCustomDashboardContext: CustomDashboardContextType = {
  setNewWidget(): void {},
  newWidget: defaultWidget,
  newDashboard: defaultDashboard,
  setNewDashboard(): void {},
  filters: defaultFilters,
  setFilters(): void {},
  isLoadingCustomDashboard: false,
  expandedWidgetId: undefined,
  setExpandedWidgetId: () => {},
  getDisplayName: () => '',
  isDashboardChanged: false,
  initDashboard: defaultDashboard,
  setInitDashboard: () => {},
  globalFilters: {},
  isAllowedEdit: false,
  initWidget: getDefaultWidget,
  metricsWithAliases: emptyArray,
  metricsClickThroughSupport: {},
  isLoadingMetricsClickThroughSupport: false,
  globalInteractiveFilters: emptyArray,
  scrollContainerRef: { current: null },
};

export const CustomDashboardContext = React.createContext(defaultCustomDashboardContext);

const CustomDashboardContextProvider = ({ children, id: dashboardId }: { children: React.ReactNode; id?: string }): React.ReactElement => {
  const {
    userPermissions: { hasAllowedEditResourcePermission },
    getPathName,
    measurements,
    calculatedDimensions,
  } = useAvContext();

  const [newWidget, setNewWidget] = useState<AllTypeWidgets>(defaultTable());
  const [filters, setFilters] = useCustomSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [expandedWidgetId, setExpandedWidgetId] = useState<string | undefined>();
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const [newDashboard, setNewDashboard] = useState<CustomDashboardDto>(() => ({
    ...defaultDashboard,
    ...getLocationState<Partial<CustomDashboardDto>>(window.history.state),
  }));
  const isAllowedEdit = hasAllowedEditResourcePermission({
    path: PAGE_PATHS.CUSTOM_DASHBOARDS,
    isNew: !dashboardId,
  });
  const { globalFilters, typeMap } = useGlobalFilters(filters);

  const [initDashboard, setInitDashboardState] = useState<CustomDashboardDto>(defaultDashboard);
  // true once onSuccess has fired and initDashboard is populated with real data.
  // For new dashboards (no dashboardId) there is no fetch, so we start as true.
  const [isDashboardLoaded, setIsDashboardLoaded] = useState(!dashboardId);

  // For new dashboards created from an imported JSON, seed the filter bar with the
  // imported globalFilters. typeMap is derived from context and ready on first render,
  // so a mount-only effect is sufficient.
  useEffect(() => {
    if (!dashboardId && Object.keys(newDashboard.globalFilters ?? {}).length > 0) {
      const importedFilters = expressionToFilter(typeMap, newDashboard.globalFilters ?? {});
      if (Object.keys(importedFilters).length > 0) {
        setFilters(prev => ({ ...prev, ...importedFilters }));
      }
    }
  }, []);

  const { data: metricsClickThroughSupportData = [], isLoading: isLoadingMetricsClickThroughSupport } = useGetMetricsClickThroughSupport();

  const metricsClickThroughSupport = useMemo(
    () =>
      metricsClickThroughSupportData.reduce(
        (acc, metric) => ({ ...acc, [metric.systemName]: metric }),
        {} as Record<string, MetricClickThroughSupport>
      ),
    [metricsClickThroughSupportData]
  );

  const metricsWithAliases = useMemo(
    () =>
      useBuildQueryParams({
        headCellsIds: [...measurements.all, ...calculatedDimensions.all].map(({ systemName }) => systemName),
        measurements: [...measurements.all, ...calculatedDimensions.all],
      }),
    [measurements.all, calculatedDimensions.all]
  );

  const globalInteractiveFilters = useGlobalInteractiveFilters();

  const setInitDashboard = (dashboard: CustomDashboardDto) => setInitDashboardState(dashboard);

  const replaceUnsaveableProps = dashboard => {
    const { widgets, ...rest } = dashboard;

    const newWidgets = widgets.map(widget => {
      const { requests, ...rest } = widget;

      const newRequests = requests.map((request, index) => {
        const { sorting, ...rest } = request;

        return {
          ...rest,
          ...(!(sorting[0]?.path && sorting[0].path.path.find(({ name }) => name === 'buckets'))
            ? { sorting }
            : { sorting: getInitialWidgetData(widget.id, initDashboard.widgets).requests[index].sorting }),
        };
      });

      return { ...rest, requests: newRequests };
    });
    return { ...rest, widgets: newWidgets };
  };

  const isDashboardChanged = useMemo(() => {
    if (Object.keys(typeMap).length === 0 || !isDashboardLoaded) {
      return false;
    }
    const savedFilters = filterToExpression(typeMap, initDashboard.globalFilters ?? {});
    return (
      !isDeepEqual(globalFilters, savedFilters) ||
      !isDeepEqual(replaceUnsaveableProps({ ...newDashboard, globalFilters }), initDashboard, [
        'isBounded',
        'isDraggable',
        'isResizable',
        'resizeHandles',
        'i',
        'maxH',
        'maxW',
        'minH',
        'minW',
        'globalFilters',
        'moved',
        'static',
        'offset',
      ])
    );
  }, [globalFilters, typeMap, newDashboard, initDashboard, isDashboardLoaded]);

  const parsePivotTableWidget = (widget: any): PivotTableTypeWidget => {
    const possiblePivotMetric = widget.requests[0].select.metrics[0];
    const isPivotMetric = possiblePivotMetric && possiblePivotMetric.name === 'pivot' && possiblePivotMetric.conf?.pivot;
    const rowDims = widget.requests[0].select.dims.map(dim => ({ ...dim, type: DimensionTypes.Row }));
    const colDims = isPivotMetric ? [{ ...possiblePivotMetric.conf.pivot.pivotDimension, type: DimensionTypes.Col }] : [];

    const { sortingCol, ...restCustom } = widget.definition.custom;

    return {
      ...widget,
      definition: { ...widget.definition, custom: restCustom },
      requests: [
        {
          ...widget.requests[0],
          sortingCol,
          select: {
            dims: parseDimsWithExpression(rowDims.concat(colDims)),
            metrics: isPivotMetric ? possiblePivotMetric.conf.pivot.metrics : widget.requests[0].select.metrics,
          },
        },
      ],
    };
  };

  const parseDashboardsWidgetsFromApiRequest = (
    dashboard: Exclude<CustomDashboardDto, 'globalFilters'>
  ): Exclude<CustomDashboardDto, 'globalFilters'> => {
    const widgets = dashboard.widgets.map(widget => {
      const parsedWidget = {
        ...widget,
        requests: widget.requests.map(request => ({
          ...request,
          select: {
            ...request.select,
            dims: parseDimsWithExpression(request.select.dims),
          },
        })),
      };
      return parsedWidget.type === TableSubType.PivotTable ? parsePivotTableWidget(parsedWidget) : parsedWidget;
    });
    return {
      ...dashboard,
      widgets,
    };
  };

  const onSuccess = (data: CustomDashboardDto) => {
    const { globalFilters: globalFiltersFromDTO, ...rest } = data;
    setNewDashboard(parseDashboardsWidgetsFromApiRequest(rest));
    const rawURLFilters = getGlobalFiltersFromDashboardFilters(filters, defaultFilters);
    const shouldTakeGlobalFiltersFromURL = Object.keys(rawURLFilters).length > 0;
    const rawSavedFilters = shouldTakeGlobalFiltersFromURL ? rawURLFilters : expressionToFilter(typeMap, globalFiltersFromDTO ?? {});
    setInitDashboard({ ...parseDashboardsWidgetsFromApiRequest(rest), globalFilters: rawSavedFilters });
    setIsDashboardLoaded(true);
    setFilters({ ...filters, ...(shouldTakeGlobalFiltersFromURL ? {} : expressionToFilter(typeMap, globalFiltersFromDTO ?? {})) });
    return data;
  };

  const onError = errors => {
    if (errors[0].extensions.status === 403) {
      enqueueSnackbar(errors[0].message, { variant: 'error' });
      navigate(getPathName(PAGE_PATHS.CUSTOM_DASHBOARDS));
    }
  };

  const { isLoading: isLoadingCustomDashboard } = useDashboard({ id: dashboardId, onSuccess, onError });

  const getDisplayName = useGetDisplayName(newWidget.requests[0].projectionId.name);
  const initWidget = useInitWidget({ projectionName: newWidget.requests[0].projectionId.name, initialDashboard: initDashboard });

  const handleSetNewWidget = (newWidget: AllTypeWidgets, { resetSize }: SetNewWidgetOptions = { resetSize: true }) => {
    const initializedRequests =
      newWidget.type === TileSubType.Tile && newWidget.requests[0].select.metrics.length !== 1
        ? getCleanRequestsFromTileWidget(newWidget)
        : newWidget.requests;

    setNewWidget({
      ...newWidget,
      requests: resetSize
        ? initializedRequests.map(r => ({ ...r, ...(r.top ? { top: { ...r.top, offset: 0 } } : r.top) }))
        : initializedRequests,
      definition:
        newWidget.type === TileSubType.Tile && newWidget.requests[0].select.metrics.length !== 1
          ? setNestedValue('custom.visualization', newWidget.definition, TileWidgetVisualization.None)
          : newWidget.definition,
    });
  };

  const value = useMemo(() => {
    const { forceRender, ...x } = filters;
    return {
      newWidget,
      setNewWidget: handleSetNewWidget,
      newDashboard,
      setNewDashboard,
      isLoadingCustomDashboard,
      filters: x,
      setFilters,
      expandedWidgetId,
      setExpandedWidgetId,
      getDisplayName,
      isDashboardChanged,
      initDashboard,
      setInitDashboard,
      globalFilters,
      isAllowedEdit,
      initWidget,
      metricsWithAliases,
      metricsClickThroughSupport,
      isLoadingMetricsClickThroughSupport,
      dashboardId,
      globalInteractiveFilters,
      scrollContainerRef,
    };
  }, [
    newWidget,
    filters,
    newDashboard,
    isLoadingCustomDashboard,
    expandedWidgetId,
    isAllowedEdit,
    metricsWithAliases,
    metricsClickThroughSupport,
    isLoadingMetricsClickThroughSupport,
    globalFilters,
    isDashboardChanged,
    initDashboard,
    globalInteractiveFilters,
    scrollContainerRef,
  ]);

  return <CustomDashboardContext.Provider value={value}>{children}</CustomDashboardContext.Provider>;
};

const useGlobalFilters = (filters: Record<string, any>) => {
  const {
    accountEntities: { fieldTypeMap },
    measurements,
    calculatedDimensions,
  } = useAvContext();

  const typeMap = useMemo(
    () => ({
      ...fieldTypeMap,
      ...(measurements?.all || []).reduce((acc, { systemName, type }) => ({ ...acc, [systemName]: type }), {}),
      ...(calculatedDimensions?.all || []).reduce((acc, { systemName, type }) => ({ ...acc, [systemName]: type }), {}),
    }),
    [fieldTypeMap, measurements, calculatedDimensions]
  );
  const globalFiltersRef = useRef<Record<string, any>>({});
  const newGlobalFilters = filterToExpression(typeMap, getGlobalFiltersFromDashboardFilters(filters, defaultFilters));
  if (!isDeepEqual(newGlobalFilters, globalFiltersRef.current)) {
    globalFiltersRef.current = newGlobalFilters;
  }
  const globalFilters = globalFiltersRef.current;
  return { typeMap, globalFilters };
};

export default CustomDashboardContextProvider;
